/**
 * Mars Scene Orchestrator
 * Coordinates all canvas modules for the complete Mars scene
 */

import {
  initSkyStars,
  renderSkyStars,
  type SkyStarsState,
} from './sky-stars';
import {
  initShootingStars,
  updateShootingStars,
  renderShootingStars,
  type ShootingStarsState,
  type ColorPalette,
} from './shooting-stars';

export type QualityTier = 'high' | 'medium' | 'low' | 'ultra';

export interface QualityConfig {
  starCount: number;
  enableShootingStars: boolean;
  enableBlurEffects: boolean;
}

export const QUALITY_CONFIGS: Record<QualityTier, QualityConfig> = {
  ultra: {
    starCount: 60000,
    enableShootingStars: true,
    enableBlurEffects: true,
  },
  high: {
    starCount: 50000,
    enableShootingStars: true,
    enableBlurEffects: true,
  },
  medium: {
    starCount: 40000,
    enableShootingStars: true,
    enableBlurEffects: true,
  },
  low: {
    starCount: 20000, // Reduced from 30000 for better mobile performance
    enableShootingStars: false, // Disabled on mobile for better performance
    enableBlurEffects: false, // Disabled on mobile for better performance
  },
};

export interface PerformanceMetrics {
  fps: number;
  frameTime: number;
  avgFrameTime: number;
  frameTimes: number[];
  lastWarningTime: number;
}

export interface MarsSceneState {
  skyStars: SkyStarsState;
  shootingStars: ShootingStarsState;
  lastTime: number;
  animationId: number | null;
  parallaxOffset?: number;
  performance?: PerformanceMetrics;
  qualityTier: QualityTier;
  qualityConfig: QualityConfig;
}

export function initMarsScene(
  canvas: HTMLCanvasElement,
  qualityTier: QualityTier = 'high',
  colorPalette?: ColorPalette
): MarsSceneState {
  const showPerfOverlay = typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('perf') === '1';

  const qualityConfig = QUALITY_CONFIGS[qualityTier];

  // Always track performance for automatic quality adjustment
  // Only show overlay when ?perf=1 is in URL
  return {
    skyStars: initSkyStars(canvas, qualityConfig.starCount),
    shootingStars: initShootingStars(canvas, colorPalette),
    lastTime: performance.now(),
    animationId: null,
    performance: {
      fps: 60,
      frameTime: 16.67,
      avgFrameTime: 16.67,
      frameTimes: [],
      lastWarningTime: 0,
      showOverlay: showPerfOverlay,
    } as PerformanceMetrics & { showOverlay?: boolean },
    qualityTier,
    qualityConfig,
  };
}

export function renderMarsScene(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  state: MarsSceneState,
  currentTime: number
): void {
  const deltaTime = Math.min(currentTime - state.lastTime, 100); // Cap deltaTime to prevent large jumps
  const frameStartTime = performance.now();

  // Only use Performance API marks when overlay is visible (to prevent memory leaks)
  const showOverlay = state.performance && (state.performance as any).showOverlay;

  if (showOverlay) {
    performance.mark('mars-scene-render-start');
  }

  state.lastTime = currentTime;

  const parallaxOffset = state.parallaxOffset || 0;

  // Render in order (back to front):
  // 1. Sky and stars (background) - with parallax offset
  if (showOverlay) {
    performance.mark('sky-stars-start');
  }
  renderSkyStars(ctx, canvas, state.skyStars, currentTime, parallaxOffset);
  if (showOverlay) {
    performance.mark('sky-stars-end');
    performance.measure('sky-stars', 'sky-stars-start', 'sky-stars-end');
  }

  // 2. Shooting stars (update and render) - fast streaks (only if enabled)
  if (state.qualityConfig.enableShootingStars) {
    if (showOverlay) {
      performance.mark('shooting-stars-update-start');
    }
    updateShootingStars(state.shootingStars, canvas, deltaTime);
    if (showOverlay) {
      performance.mark('shooting-stars-update-end');
      performance.measure('shooting-stars-update', 'shooting-stars-update-start', 'shooting-stars-update-end');
    }

    if (showOverlay) {
      performance.mark('shooting-stars-render-start');
    }
    renderShootingStars(ctx, state.shootingStars, parallaxOffset);
    if (showOverlay) {
      performance.mark('shooting-stars-render-end');
      performance.measure('shooting-stars-render', 'shooting-stars-render-start', 'shooting-stars-render-end');
    }
  }

  // Performance tracking end
  if (showOverlay) {
    performance.mark('mars-scene-render-end');
    performance.measure('mars-scene-render', 'mars-scene-render-start', 'mars-scene-render-end');
  }

  // Always track frame time for quality adjustment (but without Performance API overhead)
  if (state.performance) {
    const frameTime = performance.now() - frameStartTime;
    updatePerformanceMetrics(state.performance, frameTime);
  }
}

function updatePerformanceMetrics(perf: PerformanceMetrics, frameTime: number): void {
  // Add to rolling window (60 frames)
  perf.frameTimes.push(frameTime);
  if (perf.frameTimes.length > 60) {
    perf.frameTimes.shift();
  }

  // Calculate average frame time
  const sum = perf.frameTimes.reduce((a, b) => a + b, 0);
  perf.avgFrameTime = sum / perf.frameTimes.length;

  // Calculate FPS
  perf.fps = 1000 / perf.avgFrameTime;
  perf.frameTime = frameTime;

  // Warn if frame time exceeds 16.67ms (below 60fps) - throttle warnings to once per 5 seconds
  const now = performance.now();
  if (perf.avgFrameTime > 16.67 && now - perf.lastWarningTime > 5000) {
    console.warn(
      `[Mars Scene] Performance warning: Average frame time ${perf.avgFrameTime.toFixed(2)}ms ` +
      `(${perf.fps.toFixed(1)} FPS) - below target 60 FPS`
    );
    perf.lastWarningTime = now;
  }
}

export function startMarsSceneAnimation(
  canvas: HTMLCanvasElement,
  state: MarsSceneState,
  onFrame?: () => void
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    console.error('Failed to get 2D context');
    return;
  }

  // Type assertion: ctx is guaranteed non-null at this point
  const safeCtx: CanvasRenderingContext2D = ctx;

  function animate(time: number) {
    // Resize canvas if needed (check CSS size, not internal resolution)
    // Use Math.round to avoid fractional pixel mismatches that cause constant reinit
    const rect = canvas.getBoundingClientRect();
    // Cap DPR to prevent excessive canvas sizes at extreme zoom levels
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const expectedWidth = Math.round(rect.width * dpr);
    const expectedHeight = Math.round(rect.height * dpr);

    // Only reinitialize if dimensions changed significantly (more than 5px difference)
    // Increased threshold to reduce sensitivity to floating-point rounding
    const widthDiff = Math.abs(canvas.width - expectedWidth);
    const heightDiff = Math.abs(canvas.height - expectedHeight);

    if (widthDiff > 5 || heightDiff > 5) {
      canvas.width = expectedWidth;
      canvas.height = expectedHeight;
      // Re-get context after resizing (canvas resize resets context)
      const resizedCtx = canvas.getContext('2d');
      if (resizedCtx) {
        // Reset transform to identity matrix
        // Note: We don't use ctx.scale() - all rendering is in device pixel coordinates
        resizedCtx.setTransform(1, 0, 0, 1, 0, 0);
      }
      // Reinitialize scene with new dimensions (preserve performance state and quality tier)
      const showPerfOverlay = state.performance && (state.performance as any).showOverlay;
      const currentQualityTier = state.qualityTier || 'high';
      const newState = initMarsScene(canvas, currentQualityTier);
      // Preserve performance metrics and overlay setting
      if (state.performance && newState.performance) {
        (newState.performance as any).showOverlay = showPerfOverlay;
        // Copy existing metrics to preserve FPS history
        newState.performance.frameTimes = state.performance.frameTimes;
        newState.performance.lastWarningTime = state.performance.lastWarningTime;
      }
      Object.assign(state, newState);
    }

    renderMarsScene(safeCtx, canvas, state, time);

    // Render star gauge (always visible)
    renderStarGauge(safeCtx, canvas, state);

    // Render performance overlay if enabled (via ?perf=1 query param)
    if (state.performance && (state.performance as any).showOverlay) {
      renderPerformanceOverlay(safeCtx, canvas, state.performance);
    }

    if (onFrame) {
      onFrame();
    }

    state.animationId = requestAnimationFrame(animate);
  }

  state.animationId = requestAnimationFrame(animate);
}

export function stopMarsSceneAnimation(state: MarsSceneState): void {
  if (state.animationId !== null) {
    cancelAnimationFrame(state.animationId);
    state.animationId = null;
  }
}

function renderPerformanceOverlay(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  perf: PerformanceMetrics
): void {
  const overlayX = 10;
  const overlayY = canvas.height - 120; // Bottom left instead of top left
  const lineHeight = 20;
  const padding = 8;

  // Background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.fillRect(overlayX, overlayY, 200, 100);

  // Text style
  ctx.font = '12px monospace';
  ctx.fillStyle = '#fff';
  ctx.textBaseline = 'top';

  // FPS color based on performance
  let fpsColor = '#0f0'; // Green
  if (perf.fps < 30) {
    fpsColor = '#f00'; // Red
  } else if (perf.fps < 50) {
    fpsColor = '#ff0'; // Yellow
  }

  // Draw metrics
  ctx.fillStyle = '#fff';
  ctx.fillText('Performance Monitor', overlayX + padding, overlayY + padding);

  ctx.fillStyle = fpsColor;
  ctx.fillText(`FPS: ${perf.fps.toFixed(1)}`, overlayX + padding, overlayY + padding + lineHeight);

  ctx.fillStyle = '#fff';
  ctx.fillText(`Frame: ${perf.frameTime.toFixed(2)}ms`, overlayX + padding, overlayY + padding + lineHeight * 2);
  ctx.fillText(`Avg: ${perf.avgFrameTime.toFixed(2)}ms`, overlayX + padding, overlayY + padding + lineHeight * 3);

  // Get performance measures
  const measures = performance.getEntriesByType('measure');
  const skyStarsMeasure = measures.find(m => m.name === 'sky-stars');

  if (skyStarsMeasure) {
    ctx.fillText(`Stars: ${skyStarsMeasure.duration.toFixed(2)}ms`, overlayX + padding, overlayY + padding + lineHeight * 4);
  }

  // CRITICAL: Always clear marks and measures after each frame to prevent memory leak
  // Without this, marks/measures accumulate indefinitely causing memory exhaustion
  performance.clearMarks();
  performance.clearMeasures();
}

function renderStarGauge(
  ctx: CanvasRenderingContext2D,
  canvas: HTMLCanvasElement,
  state: MarsSceneState
): void {
  const gaugeWidth = 200;
  const gaugeHeight = 40;
  const gaugeX = canvas.width - gaugeWidth - 10; // Bottom right
  const gaugeY = canvas.height - gaugeHeight - 10; // Above performance monitor
  const padding = 8;
  const barHeight = 12;
  const barY = gaugeY + gaugeHeight - barHeight - padding;

  // Get current and max star counts
  const currentStars = state.skyStars.stars.length;
  const maxStars = state.qualityConfig.starCount;
  const starPercentage = maxStars > 0 ? currentStars / maxStars : 0;

  // Color based on star count percentage
  let barColor = '#0f0'; // Green
  if (starPercentage > 0.8) {
    barColor = '#f00'; // Red (high load)
  } else if (starPercentage > 0.5) {
    barColor = '#ff0'; // Yellow (medium)
  }

  // Tier name mapping
  const tierNames: Record<QualityTier, string> = {
    ultra: 'Ultra',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
  };

  // Background
  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
  ctx.fillRect(gaugeX, gaugeY, gaugeWidth, gaugeHeight);

  // Text style
  ctx.font = '11px monospace';
  ctx.fillStyle = '#fff';
  ctx.textBaseline = 'top';

  // Draw tier name and star count
  const tierName = tierNames[state.qualityTier] || 'Unknown';
  ctx.fillText(`Tier: ${tierName}`, gaugeX + padding, gaugeY + padding);
  ctx.fillText(`Stars: ${currentStars.toLocaleString()} / ${maxStars.toLocaleString()}`, gaugeX + padding, gaugeY + padding + 14);

  // Draw progress bar background
  ctx.fillStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.fillRect(gaugeX + padding, barY, gaugeWidth - padding * 2, barHeight);

  // Draw progress bar fill
  const barWidth = (gaugeWidth - padding * 2) * starPercentage;
  ctx.fillStyle = barColor;
  ctx.fillRect(gaugeX + padding, barY, barWidth, barHeight);
}

